import {
  ApplicationRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, ComponentFactoryResolver, ComponentRef,
  ElementRef, EventEmitter,
  Host, HostBinding, Injector, Input,
  NgModule,
  OnInit,
  Optional,
  Renderer2,
  SkipSelf, ViewChild,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import {ControlContainer} from '../../interfaces/ControlContainer'
import { ControlAccessor } from '../../models/ControlAccessor';
import { CommonModule } from '@angular/common';
import { PortalModule } from '@angular/cdk/portal';
import { AzoraRadio, AzoraRadioModule } from '../radio/radio';
import { AzoraCheckbox, AzoraCheckboxModule } from '../checkbox/checkbox';
import { LabelPosition, SelectControl, SelectOption } from './option';
import _ from 'lodash';
import { untilDestroyed } from '@azora/common';
import { Subject } from 'rxjs';

@Component({
  selector: 'az-control-group',
  template: `
      <div class="az-control-group__label">{{label}}</div>

      <ng-template #container></ng-template>
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AzoraControlGroup extends ControlAccessor implements OnInit {
  
  /** Theme color palette for the option. */
  @Input()
  get color(): string {
    return this._color;
  };
  
  set color(newColor: string) {
    if (this._color !== newColor) {
      /**Set this before proceeding to ensure no circular loop occurs with selection.*/
      this._color = newColor;
    }
  }
  
  private _color;
  
  /** Whether the labels should appear after or before the option Defaults to 'right' */
  @Input()
  private labelPosition: LabelPosition = 'right';
  
  private _selectControlsComponentType = {
    'radio': AzoraRadio,
    'checkbox': AzoraCheckbox
  };
  
  private _selectControls = [];
  
  @ViewChild('container', { read: ViewContainerRef, static: true })
  private _container;
  
  constructor(
    private _cd: ChangeDetectorRef,
    private _element: ElementRef,
    private _renderer: Renderer2,
    private _appRef: ApplicationRef,
    private _vcRef: ViewContainerRef,
    private _injector: Injector,
    private _componentFactoryResolver: ComponentFactoryResolver,
    @Optional() @Host() @SkipSelf()  parent: ControlContainer
  ) {
    super(_element, _cd);
    this.parent = parent;
  }
  
  ngOnInit() {
    super.ngOnInit();
    
    this._selectControls = _.map(this.options, (option: SelectOption) => {
      return this._loadSelectControl(option);
    });
  }
  
  onChange = (value: any) => {
    
    this.value = value;
    
    this.change.emit(value);
    
    this.control.setValue(value);
    
    this._updateSelectControlState();
  };
  
  private _loadSelectControl(option: SelectOption): ComponentRef<SelectControl> {
    
    const factory = this._componentFactoryResolver.resolveComponentFactory(this._selectControlsComponentType[this.type]);
    
    const selectControl = <ComponentRef<AzoraRadio | AzoraCheckbox>>this._container.createComponent(factory);
    
    this._parseContext(selectControl, option);
    
    return selectControl;
  }
  
  
  private _parseContext = (compRef: ComponentRef<AzoraRadio | AzoraCheckbox>, option: SelectOption) => {
    const state = {
      value: option.value,
      label: option.label,
      model: this.value,
      disabled: option.disabled ? option.disabled : this.disabled,
      color: option.color ? option.color : this.color,
      labelPosition: option.labelPosition ? option.labelPosition : this.labelPosition,
      required: option.required ? option.required : this.required,
      
      change: this.onChange
    };
    
    _.forOwn(state, (propOrEvent: any, key) => {
      if (propOrEvent instanceof Function) {
        const fn = propOrEvent;
        if (!compRef.instance[key])
          compRef.instance[key] = new Subject();
        
        compRef.instance[key]
          .pipe(untilDestroyed(this))
          .subscribe((newValue: any) => {
            fn(newValue);
          });
        
      } else if (propOrEvent !== undefined) {
        const prop = propOrEvent;
        compRef.instance[key] = prop;
      }
      ;
    });
    
  };
  
  /** Updates the `selected` radio button from the internal _value state. */
  private _updateSelectControlState(): void {
    // If the value already matches the selected radio, do nothing.
    // const isAlreadySelected = this._selected !== null && this._selected.value === this._value;
    
    _.each(this._selectControls, (compRef: ComponentRef<SelectControl>) => {
      compRef.instance.model = this.value;
    });
  }
}

const COMPONENT = [AzoraControlGroup];

@NgModule({
  imports: [
    CommonModule,
    PortalModule,
    AzoraRadioModule,
    AzoraCheckboxModule
  ],
  declarations: COMPONENT,
  exports: COMPONENT
})
export class AzoraControlGroupModule {
}
